import { useState } from 'react';
import { Music, Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import { useCardStyles } from './cardStyles';
import { attrNumber, attrString, friendlyName, humanState, useEntity } from './useEntity';

export interface MediaPlayerCardProps {
  entityId: string;
  title?: string;
}

export function MediaPlayerCard({ entityId, title }: MediaPlayerCardProps): JSX.Element {
  const styles = useCardStyles();
  const entity = useEntity(entityId);
  const state = entity?.state ?? 'off';
  const s = humanState(entity);
  const mediaTitle = attrString(entity, 'media_title');
  const mediaArtist = attrString(entity, 'media_artist');
  const volume = attrNumber(entity, 'volume_level');
  const [optimisticPlaying, setOptimisticPlaying] = useState<boolean | null>(null);

  const isPlaying = optimisticPlaying ?? state === 'playing';

  return (
    <div
      className={styles.container}
      data-domain="media_player"
      data-entity-id={entityId}
      data-state={state}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${
            isPlaying
              ? 'bg-purple-500/20 text-purple-500 dark:text-purple-400'
              : styles.isDark
              ? 'bg-gray-700/60 text-gray-500'
              : 'bg-gray-100 text-gray-400'
          }`}
        >
          <Music className="h-5 w-5" />
        </div>
        <span
          className={`text-xs uppercase tracking-wider ${
            isPlaying ? 'text-purple-500 dark:text-purple-400' : styles.subtle
          }`}
        >
          {s.text}
        </span>
      </div>

      <div className="min-h-[2.5rem]">
        <div className={`truncate text-sm font-semibold ${styles.title}`}>
          {mediaTitle ?? friendlyName(entity, title)}
        </div>
        {mediaArtist && (
          <div className={`truncate text-xs ${styles.subtle}`}>{mediaArtist}</div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          aria-label="Zurück"
          className={`rounded-lg p-1.5 ${styles.muted} hover:bg-[var(--rsc-primary)]/10 hover:text-[var(--rsc-primary)]`}
        >
          <SkipBack className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={isPlaying ? 'Pausieren' : 'Wiedergabe'}
          onClick={() => setOptimisticPlaying(!isPlaying)}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--rsc-primary)] text-white shadow-md hover:brightness-110"
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <button
          type="button"
          aria-label="Weiter"
          className={`rounded-lg p-1.5 ${styles.muted} hover:bg-[var(--rsc-primary)]/10 hover:text-[var(--rsc-primary)]`}
        >
          <SkipForward className="h-4 w-4" />
        </button>
      </div>

      {volume !== undefined && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between text-xs">
            <span className={styles.label}>Lautstärke</span>
            <span className={`${styles.muted} tabular-nums`}>{Math.round(volume * 100)}%</span>
          </div>
          <div className={`h-1.5 w-full overflow-hidden rounded-full ${styles.isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className="h-full rounded-full bg-purple-400 transition-all"
              style={{ width: `${Math.min(100, Math.max(0, volume * 100))}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
